import { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar } from 'lucide-react';
import { TRIGGER_CONTENT, triggerClass, useDropUp, type NotionVariant } from './notionControl';

interface NotionDatePickerProps {
  value: string; // YYYY-MM-DD format
  onChange: (value: string) => void;
  className?: string;
  variant?: NotionVariant;
}

const CALENDAR_HEIGHT = 330;

export function NotionDatePicker({
  value,
  onChange,
  className = '',
  variant = 'field',
}: NotionDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedDate = value ? new Date(value + 'T00:00:00') : new Date();
  const dropUp = useDropUp(isOpen, triggerRef, CALENDAR_HEIGHT);

  // Kept off the drawer's Escape handler — see the note in NotionSelect.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  const handleChange = (date: Date | null) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
      setIsOpen(false);
    }
  };

  const formatDisplayDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className={variant === 'field' ? 'relative block w-full' : 'relative inline-block'}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={triggerClass(variant, className)}
      >
        <span className={TRIGGER_CONTENT}>
          {value ? formatDisplayDate(value) : <span className="text-zinc-400">Select date</span>}
        </span>
        <Calendar size={15} className="shrink-0 text-zinc-400" aria-hidden />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div
            className={`notion-datepicker-wrapper absolute z-50 ${
              dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            <DatePicker
              selected={selectedDate}
              onChange={handleChange}
              inline
              calendarClassName="notion-datepicker"
            />
          </div>
        </>
      )}

      {/*
        The calendar came in on react-datepicker's own stylesheet and was only
        half re-skinned: selected day and today were both hard-coded violet
        (#7c3aed), a colour that exists nowhere else in the portal. Ink for the
        selection, monospace digits to match how every other date in a drawer
        is set.
      */}
      <style>{`
        .notion-datepicker-wrapper {
          background: white;
          border: 1px solid rgb(228, 228, 231);
          border-radius: 12px;
          box-shadow: 0 12px 32px rgba(30, 34, 40, 0.12);
          padding: 8px;
          overflow: hidden;
        }

        .notion-datepicker {
          font-family: inherit;
          border: none;
        }

        .react-datepicker {
          font-family: inherit;
          border: none;
          background: transparent;
        }

        .react-datepicker__header {
          background: transparent;
          border: none;
          padding-top: 8px;
        }

        .react-datepicker__current-month {
          font-weight: 700;
          font-size: 14px;
          letter-spacing: -0.01em;
          color: #1e2228;
          margin-bottom: 12px;
        }

        .react-datepicker__day-names {
          margin-bottom: 4px;
        }

        .react-datepicker__day-name {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          color: rgb(113, 113, 122);
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          width: 36px;
          line-height: 36px;
          margin: 2px;
        }

        .react-datepicker__day {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          width: 36px;
          line-height: 36px;
          margin: 2px;
          color: #1e2228;
          border-radius: 8px;
          font-size: 12.5px;
          transition: background-color 0.15s, color 0.15s;
        }

        .react-datepicker__day:hover {
          background: rgb(244, 244, 245);
          border-radius: 8px;
        }

        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background: #1e2228 !important;
          color: white !important;
          font-weight: 500;
          border-radius: 8px;
        }

        .react-datepicker__day--outside-month {
          color: rgb(212, 212, 216);
        }

        .react-datepicker__day--disabled {
          color: rgb(212, 212, 216);
          cursor: not-allowed;
        }

        .react-datepicker__navigation {
          top: 14px;
        }

        .react-datepicker__navigation-icon::before {
          border-color: rgb(113, 113, 122);
          border-width: 2px 2px 0 0;
          height: 7px;
          width: 7px;
        }

        .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
          border-color: #1e2228;
        }

        .react-datepicker__day--today {
          font-weight: 700;
          box-shadow: inset 0 0 0 1px rgb(212, 212, 216);
        }

        .react-datepicker__day--today.react-datepicker__day--selected {
          box-shadow: none;
        }
      `}</style>
    </div>
  );
}
